import * as React from 'react';
import loadable from '@loadable/component';
import { useSelector } from 'react-redux';
import { Modal } from '@cian/ui-kit/modal';
import { Header3 } from '@cian/ui-kit/typography';
import { GeoSwitcher } from '../../components/GeoSwitcher';
import { LoadableErrorBoundary } from '../../components/LoadableErrorBoundary';
import { LoadableFallback } from '../../components/LoadableFallback';
import { trackGeoSwitcherClick } from './tracking';
import { IApplicationState } from '../../types/redux';
import { IGeoSwitcherState } from '../../types/geoSwitcher';
import { selectPageKind } from '../../selectors/pageKind';

import * as styles from './GeoSwitcherWrapperContainer.css';

const GeoSwitcherContainerInnerLoadable = loadable(
  () => import(/* webpackChunkName: "GeoSwitcherContainerInnerLoadable" */ './GeoSwitcherContainer'),
  {
    ssr: false,
    fallback: <LoadableFallback />,
  },
);

export const GeoSwitcherWrapperContainer = () => {
  const [open, setOpen] = React.useState(false);
  const titleId = React.useId();
  const descriptionId = `${titleId}-description`;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  const pageKind = useSelector(selectPageKind);
  const { region, popularCities } = useSelector<IApplicationState, IGeoSwitcherState>(state => state.geoSwitcher);

  const handleOpenGeoSwitcher = React.useCallback(() => {
    setOpen(true);
    trackGeoSwitcherClick(pageKind, region?.displayName);
  }, [pageKind, region]);

  const handleCloseGeoSwitcher = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpenAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault();
    closeButtonRef.current?.focus();
  }, []);

  const handleCloseAutoFocus = React.useCallback((event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  }, []);

  const handleEscapeKeyDown = React.useCallback((event: KeyboardEvent) => {
    if (event.isComposing || event.repeat) {
      event.preventDefault();

      return;
    }

    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    const target = event.target;

    // Modal обрабатывает Escape в capture-фазе, раньше обработчика поля.
    if (
      target instanceof HTMLElement &&
      target.getAttribute('role') === 'combobox' &&
      target.getAttribute('aria-expanded') === 'true'
    ) {
      event.preventDefault();
    }
  }, []);

  const retryLoadContent = React.useCallback(() => {
    GeoSwitcherContainerInnerLoadable.load();
  }, []);

  if (popularCities.length === 0 || !region) {
    return null;
  }

  return (
    <>
      <GeoSwitcher title={region.displayName} buttonRef={triggerRef} onClick={handleOpenGeoSwitcher} />
      <Modal
        open={open}
        onOpenChange={setOpen}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        onEscapeKeyDown={handleEscapeKeyDown}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        closeButtonProps={{ ref: closeButtonRef, title: 'Закрыть' }}
        className={styles['modal']}
        width={1131}
        size="M"
      >
        <div className={styles['content']}>
          <div id={titleId} className={styles['title']}>
            <Header3>Выберите регион или город</Header3>
          </div>
          <p id={descriptionId} className={styles['description']}>
            Введите название города или выберите его из списка.
          </p>
          <LoadableErrorBoundary onRetry={retryLoadContent}>
            <GeoSwitcherContainerInnerLoadable closeGeoSuggest={handleCloseGeoSwitcher} titleId={titleId} />
          </LoadableErrorBoundary>
        </div>
      </Modal>
    </>
  );
};
